import { groupBy } from 'lodash';
import { FieldSectionSubType } from 'api/field-sections/types';
import { HttpClient } from 'api/http-client';
import { ObjectType } from 'api/pushes';
import { CustomFieldValueSourceType, Field } from 'api/tasks';
import { Color } from 'components/colors';
import { ReportType } from 'components/pages/custom-reports/types';

export interface AccountFields {
  accountId: number | null;
  accountName: string | null;
  objectType: string | null;
  fields: Array<Field>;
}

export type PaginatedFieldsArgs = {
  offset?: number;
  limit?: number;
  lastRecordPointer?: number;
};

export type PaginatedFieldsResponse = {
  data: Field[];
  count: number;
  nextPage: {
    limit: number;
    offset: number;
  };
};

export type FieldMetaDataOption = {
  color: Color;
  label: string;
  position?: number;
  group?: number;
  deletable?: boolean;
  editable?: boolean;
};

export type RatingFieldScale = 3 | 5 | 7 | 10;
export const RATING_SCALE = [3, 5, 7, 10];

export type RatingFieldMeta = {
  options: FieldMetaDataOption[];
  ratingScale?: number;
};

export type InvoiceFieldMeta = {
  visibleOnInvoice: boolean;
};

export type TimeEntryFieldMeta = {
  fieldValueSourceFieldInfo?: Field;
  isMandatory?: boolean;
  fieldValueSourceType?: CustomFieldValueSourceType;
  fallbackFieldValue?: any;
};

export interface SingleAndMultiSelectFieldMeta {
  options: FieldMetaDataOption[];
}
export interface NoteFieldMeta {
  tags: FieldMetaDataOption[];
}

export type KeyEventMeta = {
  captureKeyIntervalForFirstActivityOnly: boolean;
  allowAdminsAndOwnersToEdit: boolean;
};

export type IntervalMeta = {
  startKeyEventId: number;
  endKeyEventId: number;
  intervalBenchmark: number;
};

// the will hold info related to the frontend context in which the formula field is shown
export type FormulaFieldContextMeta = {
  formulaFieldDetails: {
    customReportType: ReportType;
  };
};

export type FieldMetaData =
  | RatingFieldMeta
  | KeyEventMeta
  | IntervalMeta
  | SingleAndMultiSelectFieldMeta
  | NoteFieldMeta
  | InvoiceFieldMeta
  | FormulaFieldContextMeta
  | TimeEntryFieldMeta;

interface Payload
  extends Pick<
    Field,
    | 'isEnabled'
    | 'fieldLabel'
    | 'description'
    | 'private'
    | 'fieldDataType'
    | 'fieldType'
    | 'fieldTypeDisplay'
    | 'objectType'
    | 'objectSubType'
    | 'fieldSectionId'
    | 'formulaFieldMeta'
  > {
  metaData?: FieldMetaData;
  value?: number;
}

export type FieldPayload = Partial<Payload>;

function getUrl(fieldId: number) {
  return `/fields/${fieldId}`;
}

export interface PaginatedFieldsRequest {
  offset: number;
  limit: number;
}

export type FieldsResponse = {
  data: Field[];
  count: number;
  nextPage?: number;
};

const addIndexToFieldsGroupedByObjectType = (
  accountFields: AccountFields[],
): AccountFields[] => {
  return accountFields.map((accountField) => {
    const sortedFields = accountField.fields?.sort(
      ({ fieldId: fieldIdA }, { fieldId: fieldIdB }) => fieldIdA - fieldIdB,
    );

    /**
     * Doing special handling for INVOICE_PROJECT fields because
     * BE populates the fieldAccessMeta in PROJECT instead of INVOICE_PROJECT for these fields.
     * So even we have to populate index for it in PROJECT.
     */
    const fieldsGrouped = groupBy(sortedFields, (field) =>
      field.objectSubType &&
      field.objectSubType !== FieldSectionSubType.Default &&
      field.objectSubType !== FieldSectionSubType.InvoiceProject
        ? field.objectSubType
        : field.objectType,
    );

    accountField.fields = Object.values(fieldsGrouped).reduce(
      (allFields, fieldsInGroup) =>
        allFields.concat(
          fieldsInGroup.map((field, index) => ({
            ...field,
            fieldIndex: index,
          })),
        ),
      [],
    );

    return accountField;
  });
};

export interface FieldResourceTypeForRbac {
  objectType: ObjectType;
  objectId: number;
}

export enum FieldsObjectTypeForApi {
  Tasks = 'tasks',
  Projects = 'projects',
  Expense = 'expense',
  Invoice = 'invoice',
  CreditNote = 'credit-note',
  Users = 'users',
  TimeEntry = 'time-entry',
  Company = 'company',
  Sprint = 'sprint',
  Epic = 'epic',
  Backlog = 'backlog',
  Sheet = 'sheet',
  Intervals = 'intervals',
  KeyEvents = 'key-events',
  ProjectPhase = 'projectPhases',
  Reports = 'reports',
  Financial = 'financial',
  Budget = 'project-financials-budget',
  Timeoff = 'time-off',
  ResourceAllocation = 'resource-allocation',
}

export enum FieldSectionSubTypeForApi {
  Default = 'DEFAULT',
  Skill = 'SKILL',
  ExternalUser = 'EXTERNAL_TEAM_MEMBER',
  InvoiceProject = 'INVOICE_PROJECT',
  CustomerUser = 'CUSTOMER_USER',
}

class FieldsHttpClient extends HttpClient {
  public getAllFields = async () => {
    const data: AccountFields[] = await this.get<AccountFields[]>(
      '/all-fields',
    );

    return addIndexToFieldsGroupedByObjectType(data);
  };

  public async updateField(
    fieldId: number,
    data: FieldPayload,
  ): Promise<Field> {
    return this.put(getUrl(fieldId), data);
  }

  public async getField(fieldId: number): Promise<Field> {
    return this.get(getUrl(fieldId));
  }

  public async deleteField(fieldId: number): Promise<Field> {
    return this.delete(getUrl(fieldId));
  }

  public async createField(data: FieldPayload): Promise<Field> {
    return this.post('/fields', data);
  }

  public async getFieldProjectMapping(
    fieldId: number,
    value: number | undefined,
  ): Promise<Field> {
    return this.get(
      `${getUrl(fieldId)}/getIsStatusDeletePossible?value=${value}`,
    );
  }

  public getSkillFieldIdsForReadAccess = async (
    resource?: string,
  ): Promise<Field['fieldId'][]> => {
    return this.get(`/skill/fields${resource ? `?resource=${resource}` : ''}`);
  };

  public getTimeEntryFields = async (): Promise<AccountFields> => {
    return this.get<{ fields: AccountFields }>('/time-entry/fields').then(
      (data) => {
        return data.fields;
      },
    );
  };

  public getExpenseFields = async (): Promise<AccountFields> => {
    return this.get<{ fields: AccountFields }>('/expense/fields').then(
      (data) => {
        return data.fields;
      },
    );
  };

  public getPaginatedFields = async (
    objectType: ObjectType,
    args?: {
      offset?: number;
      limit?: number;
      lastRecordPointer?: number;
      objectSubType?: FieldSectionSubType;
      fieldLabel?: string;
      fieldDataType?: string[];
      fieldType?: string;
      fieldSectionId?: number;
      userCreated?: boolean;
      includedFieldIds?: number[];
      excludedFieldIds?: number[];
      includedFieldNames?: string[];
      excludedFieldNames?: string[];
      optionsAvailable?: boolean;
    },
    resource?: FieldResourceTypeForRbac | undefined,
  ): Promise<PaginatedFieldsResponse> => {
    const params = {
      objectType,
      ...(args ?? {}),
      resource,
    };

    return this.post<PaginatedFieldsResponse, typeof params>(
      '/fields/searchLight',
      params,
    );
  };

  async getFieldsByObjectType(
    objectType: FieldsObjectTypeForApi,
    objectSubType: FieldSectionSubTypeForApi = FieldSectionSubTypeForApi.Default,
    ignoreIndexing: boolean = false,
  ): Promise<AccountFields> {
    const query = `?objectSubType=${objectSubType}`;
    const resp = await this.get<AccountFields>(`/${objectType}/fields${query}`);

    return ignoreIndexing
      ? resp
      : addIndexToFieldsGroupedByObjectType([resp])?.[0];
  }
}

export const FieldsAPI = new FieldsHttpClient();
